import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelectedClient } from "../contexts/SelectedClientContext";
import { useTimer } from "../contexts/TimerContext";
import {
  clientsAPI,
  projectsAPI,
  tasksAPI,
  timeEntriesAPI,
  quotesAPI,
  invoicesAPI,
  clientLinksAPI,
  contactsAPI,
  exportAPI,
} from "../lib/api";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDuration,
  getStatusColor,
  getPriorityColor,
  isWithinShift,
  PROJECT_STATUSES,
  TASK_STATUSES,
  TASK_PRIORITIES,
  QUOTE_STATUSES,
  INVOICE_STATUSES,
} from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  ExternalLink,
  Clock,
  Play,
  Square,
  X,
  Sun,
  Moon,
  FileText,
  Receipt,
  Link as LinkIcon,
  User,
  Download,
  BarChart2,
} from "lucide-react";

export default function ClientWorkspacePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { setSelectedClient } = useSelectedClient();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Data states
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [links, setLinks] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Dialog states
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);

  // Form states
  const [editingProject, setEditingProject] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    fetchClient();
    fetchAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Clear selected client when leaving this page
  useEffect(() => {
    return () => {
      setSelectedClient(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClient = async () => {
    try {
      const res = await clientsAPI.get(clientId);
      setClient(res.data);
      setSelectedClient(res.data);
    } catch (err) {
      toast.error("Client not found");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    const params = { client_id: clientId };
    try {
      const [projectsRes, tasksRes, timeRes, quotesRes, invoicesRes, linksRes, contactsRes] = await Promise.all([
        projectsAPI.list(params),
        tasksAPI.list(params),
        timeEntriesAPI.list(params),
        quotesAPI.list(params),
        invoicesAPI.list(params),
        clientLinksAPI.list(params),
        contactsAPI.list(params),
      ]);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      setTimeEntries(timeRes.data);
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
      setLinks(linksRes.data);
      setContacts(contactsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  // Calculate totals
  const totalBillableMinutes = timeEntries
    .filter((e) => e.billable)
    .reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  const totalBillableHours = Math.round(totalBillableMinutes / 60 * 10) / 10;
  const totalInvoiced = invoices.reduce((sum, i) => sum + (i.total || 0), 0);
  const totalPaid = invoices.reduce((sum, i) => sum + (i.paid_amount || 0), 0);
  const totalOutstanding = totalInvoiced - totalPaid;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-workspace">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/clients")} data-testid="back-to-clients">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">{client.name}</h1>
            <Badge className={getStatusColor(client.status)}>{client.status}</Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            {client.email && <span>{client.email}</span>}
            {client.email && client.phone && <span className="mx-2">•</span>}
            {client.phone && <span>{client.phone}</span>}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Projects</p>
            <p className="text-2xl font-bold mt-1">{projects.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Billable Hours</p>
            <p className="text-2xl font-bold mt-1 font-mono">{totalBillableHours}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Invoiced</p>
            <p className="text-2xl font-bold mt-1 font-mono">{formatCurrency(totalInvoiced, client.currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Outstanding</p>
            <p className="text-2xl font-bold mt-1 font-mono text-yellow-600">{formatCurrency(totalOutstanding, client.currency)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
          {["overview", "projects", "tasks", "tracker", "time", "quotes", "invoices", "links", "contacts"].map((tab) => (
            <TabsTrigger
              key={tab}
              value={tab}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-accent data-[state=active]:bg-transparent px-4 py-3 capitalize"
              data-testid={`tab-${tab}`}
            >
              {tab}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {client.address && (
                  <div>
                    <p className="text-xs text-muted-foreground">Address</p>
                    <p className="font-medium">{client.address}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Currency</p>
                  <p className="font-medium">{client.currency}</p>
                </div>
                {(client.billing_modes || ["hourly"]).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground">Billing</p>
                    <p className="font-medium font-mono">
                      {(() => {
                        const modes = client.billing_modes || ["hourly"];
                        const parts = [];
                        if (modes.includes("hourly") && client.default_rate > 0)
                          parts.push(`${formatCurrency(client.default_rate, client.currency)}/hr`);
                        if (modes.includes("monthly") && client.monthly_rate > 0)
                          parts.push(`${formatCurrency(client.monthly_rate, client.currency)}/mo`);
                        if (modes.includes("commission"))
                          parts.push("Commission");
                        return parts.length > 0 ? parts.join(" · ") : "—";
                      })()}
                    </p>
                  </div>
                )}
                {(client.shift_start || client.shift_end) && (
                  <div>
                    <p className="text-xs text-muted-foreground">Work Shift</p>
                    <p className="font-medium">{client.shift_start || "?"} - {client.shift_end || "?"}</p>
                  </div>
                )}
                {client.notes && (
                  <div>
                    <p className="text-xs text-muted-foreground">Notes</p>
                    <p className="text-sm">{client.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {timeEntries.length > 0 ? (
                  <div className="space-y-3">
                    {timeEntries.slice(0, 5).map((entry) => (
                      <div key={entry.id} className="flex justify-between text-sm">
                        <span className="truncate flex-1">{entry.description || "Time entry"}</span>
                        <span className="font-mono text-muted-foreground ml-2">
                          {formatDuration(entry.duration_minutes)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No time entries yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="mt-6">
          <ProjectsSection
            projects={projects}
            clientId={clientId}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-6">
          <TasksSection
            tasks={tasks}
            projects={projects}
            clientId={clientId}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Tracker Tab */}
        <TabsContent value="tracker" className="mt-6">
          <ProjectTrackerSection
            tasks={tasks}
            projects={projects}
            timeEntries={timeEntries}
            clientId={clientId}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Time Tab */}
        <TabsContent value="time" className="mt-6">
          <TimeSection
            timeEntries={timeEntries}
            client={client}
            projects={projects}
            tasks={tasks}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes" className="mt-6">
          <QuotesSection
            quotes={quotes}
            client={client}
            tasks={tasks}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-6">
          <InvoicesSection
            invoices={invoices}
            client={client}
            quotes={quotes}
            timeEntries={timeEntries}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Links Tab */}
        <TabsContent value="links" className="mt-6">
          <LinksSection
            links={links}
            clientId={clientId}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="mt-6">
          <ContactsSection
            contacts={contacts}
            clientId={clientId}
            onRefresh={fetchAllData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Projects Section Component
function ProjectsSection({ projects, clientId, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    rate: "",
    status: "active",
    deadline: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, client_id: clientId, rate: formData.rate ? parseFloat(formData.rate) : null };
      if (editing) {
        await projectsAPI.update(editing.id, data);
        toast.success("Project updated");
      } else {
        await projectsAPI.create(data);
        toast.success("Project created");
      }
      setShowDialog(false);
      setEditing(null);
      setFormData({ name: "", description: "", rate: "", status: "active", deadline: "" });
      onRefresh();
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await projectsAPI.delete(id);
      toast.success("Project deleted");
      onRefresh();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (project) => {
    setEditing(project);
    setFormData({
      name: project.name,
      description: project.description || "",
      rate: project.rate || "",
      status: project.status,
      deadline: project.deadline || "",
    });
    setShowDialog(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Projects</h2>
        <Button onClick={() => { setEditing(null); setFormData({ name: "", description: "", rate: "", status: "active", deadline: "" }); setShowDialog(true); }} data-testid="add-project-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No projects yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-medium">{project.name}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(project.status)}>{project.status}</Badge>
                  </TableCell>
                  <TableCell className="font-mono">{project.rate ? `${project.rate}/hr` : "-"}</TableCell>
                  <TableCell>{project.deadline ? formatDate(project.deadline) : "-"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(project)}>
                          <Pencil className="h-4 w-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(project.id)} className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Project" : "Add Project"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="project-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate/hr</Label>
                <Input type="number" step="0.01" value={formData.rate} onChange={(e) => setFormData({...formData, rate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Deadline</Label>
              <Input type="date" value={formData.deadline} onChange={(e) => setFormData({...formData, deadline: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Tasks Section Component
function TasksSection({ tasks, projects, clientId, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    project_id: "",
    rate: "",
    estimated_hours: "",
    status: "pending",
    priority: "medium",
    due_date: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        client_id: clientId,
        project_id: formData.project_id || null,
        rate: formData.rate ? parseFloat(formData.rate) : null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
      };
      if (editing) {
        await tasksAPI.update(editing.id, data);
        toast.success("Task updated");
      } else {
        await tasksAPI.create(data);
        toast.success("Task created");
      }
      setShowDialog(false);
      setEditing(null);
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", project_id: "", rate: "", estimated_hours: "", status: "pending", priority: "medium", due_date: "" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await tasksAPI.delete(id);
      toast.success("Task deleted");
      onRefresh();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (task) => {
    setEditing(task);
    setFormData({
      title: task.title,
      description: task.description || "",
      project_id: task.project_id || "",
      rate: task.rate || "",
      estimated_hours: task.estimated_hours || "",
      status: task.status,
      priority: task.priority,
      due_date: task.due_date || "",
    });
    setShowDialog(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Tasks</h2>
        <Button onClick={() => { setEditing(null); resetForm(); setShowDialog(true); }} data-testid="add-task-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Task
        </Button>
      </div>

      {tasks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No tasks yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Est. Hours</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((task) => (
                <TableRow key={task.id}>
                  <TableCell className="font-medium">{task.title}</TableCell>
                  <TableCell>{projects.find(p => p.id === task.project_id)?.name || "-"}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(task.status)}>{task.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className={getPriorityColor(task.priority)}>{task.priority}</span>
                  </TableCell>
                  <TableCell className="font-mono">{task.estimated_hours || "-"}</TableCell>
                  <TableCell>{task.due_date ? formatDate(task.due_date) : "-"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(task)}>
                          <Pencil className="h-4 w-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(task.id)} className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Task" : "Add Task"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="task-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={formData.project_id} onValueChange={(v) => setFormData({...formData, project_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate/hr</Label>
                <Input type="number" step="0.01" value={formData.rate} onChange={(e) => setFormData({...formData, rate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Est. Hours</Label>
                <Input type="number" step="0.5" value={formData.estimated_hours} onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Time Section Component
function TimeSection({ timeEntries, client, projects, tasks, onRefresh }) {
  const { getTimerForClient, formatElapsedForClient, startTimer, stopTimer, discardTimer } = useTimer();
  const clientTimer = getTimerForClient(client.id);
  const isClientTimerRunning = !!clientTimer;

  const [selectedProject, setSelectedProject] = useState("");
  const [selectedTask, setSelectedTask] = useState("");
  const [description, setDescription] = useState("");

  // Manual entry dialog state
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({
    project_id: "",
    task_id: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    start_time: "09:00",
    end_time: "10:00",
    billable: true,
  });

  const handleStartTimer = async () => {
    try {
      const taskId = selectedTask && selectedTask !== "none" ? selectedTask : null;
      await startTimer({
        client_id: client.id,
        project_id: selectedProject && selectedProject !== "none" ? selectedProject : null,
        task_id: taskId,
        description: description || null,
      });
      // The backend auto-promotes "new" → "in_progress"; refresh so tracker reflects it
      if (taskId) {
        onRefresh();
      }
      toast.success("Timer started");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start timer");
    }
  };

  const handleStopTimer = async () => {
    try {
      await stopTimer(client.id);
      toast.success("Time entry saved");
      onRefresh();
    } catch (err) {
      toast.error("Failed to stop timer");
    }
  };

  const handleDiscardTimer = async () => {
    if (!window.confirm("Discard current timer?")) return;
    try {
      await discardTimer(client.id);
      toast.success("Timer discarded");
    } catch (err) {
      toast.error("Failed to discard timer");
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      const startDateTime = new Date(`${manualForm.date}T${manualForm.start_time}:00`);
      const endDateTime = new Date(`${manualForm.date}T${manualForm.end_time}:00`);
      const durationMinutes = Math.floor((endDateTime - startDateTime) / 60000);

      if (durationMinutes <= 0) {
        toast.error("End time must be after start time");
        return;
      }

      await timeEntriesAPI.create({
        client_id: client.id,
        project_id: manualForm.project_id && manualForm.project_id !== "none" ? manualForm.project_id : null,
        task_id: manualForm.task_id && manualForm.task_id !== "none" ? manualForm.task_id : null,
        description: manualForm.description || null,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        duration_minutes: durationMinutes,
        billable: manualForm.billable,
      });
      toast.success("Time entry added");
      setShowManualDialog(false);
      setManualForm({
        project_id: "",
        task_id: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        start_time: "09:00",
        end_time: "10:00",
        billable: true,
      });
      onRefresh();
    } catch (err) {
      toast.error("Failed to add time entry");
    }
  };

  return (
    <div className="space-y-4">
      {/* Timer Widget */}
      <Card className={isClientTimerRunning ? "border-brand-accent" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {isClientTimerRunning ? "Timer Running" : "Track Time"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isClientTimerRunning ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="timer-display timer-running font-mono text-4xl font-bold" data-testid="client-timer-display">
                  {formatElapsedForClient(client.id)}
                </p>
                {clientTimer.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{clientTimer.description}</p>
                )}
              </div>
              <div className="flex justify-center gap-4">
                <Button onClick={handleStopTimer} className="gap-2" data-testid="client-stop-timer-btn">
                  <Square className="h-4 w-4 fill-current" />
                  Stop & Save
                </Button>
                <Button variant="outline" onClick={handleDiscardTimer} data-testid="client-discard-timer-btn">
                  <X className="h-4 w-4 mr-2" />
                  Discard
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger data-testid="client-timer-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Task</Label>
                  <Select value={selectedTask} onValueChange={setSelectedTask}>
                    <SelectTrigger data-testid="client-timer-task-select">
                      <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Task</SelectItem>
                      {tasks.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What are you working on?"
                    data-testid="client-timer-description-input"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <Button onClick={handleStartTimer} className="gap-2" data-testid="client-start-timer-btn">
                  <Play className="h-4 w-4" />
                  Start Timer
                </Button>
                <Button variant="outline" onClick={() => setShowManualDialog(true)} className="gap-2" data-testid="client-manual-entry-btn">
                  <Plus className="h-4 w-4" />
                  Manual Entry
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Time Entries</h2>
      </div>

      {timeEntries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No time entries yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Shift</TableHead>
                <TableHead>Billable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timeEntries.map((entry) => {
                const inShift = isWithinShift(entry.start_time, client.shift_start, client.shift_end);
                return (
                  <TableRow key={entry.id}>
                    <TableCell>{formatDateTime(entry.start_time)}</TableCell>
                    <TableCell>{entry.description || "-"}</TableCell>
                    <TableCell>{projects.find(p => p.id === entry.project_id)?.name || "-"}</TableCell>
                    <TableCell className="font-mono">{formatDuration(entry.duration_minutes)}</TableCell>
                    <TableCell>
                      {inShift === null ? "-" : inShift ? (
                        <Sun className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <Moon className="h-4 w-4 text-blue-500" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={entry.billable ? "default" : "secondary"}>
                        {entry.billable ? "Yes" : "No"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Manual Entry Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Time Entry</DialogTitle>
            <DialogDescription>
              Record time for {client.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={manualForm.description}
                onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                placeholder="What did you work on?"
              />
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <Select
                value={manualForm.project_id}
                onValueChange={(v) => setManualForm({ ...manualForm, project_id: v, task_id: "" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={manualForm.date}
                  onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={manualForm.start_time}
                  onChange={(e) => setManualForm({ ...manualForm, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={manualForm.end_time}
                  onChange={(e) => setManualForm({ ...manualForm, end_time: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="client-billable"
                checked={manualForm.billable}
                onChange={(e) => setManualForm({ ...manualForm, billable: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="client-billable">Billable</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowManualDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Add Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Quotes Section Component
function QuotesSection({ quotes, client, tasks, onRefresh }) {
  const navigate = useNavigate();
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Quotes</h2>
        <Button onClick={() => navigate(`/quotes/new?client_id=${client.id}`)} data-testid="create-quote-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Quote
        </Button>
      </div>

      {quotes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No quotes yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quote #</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((quote) => (
                <TableRow key={quote.id}>
                  <TableCell className="font-mono">{quote.quote_number}</TableCell>
                  <TableCell>{quote.title}</TableCell>
                  <TableCell className="font-mono">{formatCurrency(quote.total, quote.currency)}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(quote.status)}>{quote.status}</Badge>
                  </TableCell>
                  <TableCell>{quote.valid_until ? formatDate(quote.valid_until) : "-"}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/quotes/${quote.id}`)}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// Invoices Section Component
function InvoicesSection({ invoices, client, quotes, timeEntries, onRefresh }) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Invoices</h2>
        <Button onClick={() => navigate(`/invoices/new?client_id=${client.id}`)} data-testid="create-invoice-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {invoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No invoices yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                  <TableCell>{invoice.title}</TableCell>
                  <TableCell className="font-mono">{formatCurrency(invoice.total, invoice.currency)}</TableCell>
                  <TableCell className="font-mono text-green-600">{formatCurrency(invoice.paid_amount, invoice.currency)}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                  </TableCell>
                  <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : "-"}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// Links Section Component
function normalizeUrl(url) {
  if (!url) return "";
  const trimmed = url.trim();
  if (trimmed && !trimmed.startsWith("http://") && !trimmed.startsWith("https://") && !trimmed.startsWith("ftp://")) {
    return "https://" + trimmed;
  }
  return trimmed;
}

function LinksSection({ links, clientId, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ label: "", url: "", category: "", notes: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const normalizedData = { ...formData, url: normalizeUrl(formData.url) || formData.url };
      if (editing) {
        await clientLinksAPI.update(editing.id, normalizedData);
        toast.success("Link updated");
      } else {
        await clientLinksAPI.create({ ...normalizedData, client_id: clientId });
        toast.success("Link added");
      }
      setShowDialog(false);
      setEditing(null);
      setFormData({ label: "", url: "", category: "", notes: "" });
      onRefresh();
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await clientLinksAPI.delete(id);
      toast.success("Link deleted");
      onRefresh();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Links Vault</h2>
        <Button onClick={() => { setEditing(null); setFormData({ label: "", url: "", category: "", notes: "" }); setShowDialog(true); }} data-testid="add-link-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Link
        </Button>
      </div>

      {links.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <LinkIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No links saved yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {links.map((link) => (
            <Card key={link.id} className="group hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <a href={normalizeUrl(link.url) || link.url} target="_blank" rel="noopener noreferrer" className="font-medium hover:text-blue-500 flex items-center gap-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      {link.label}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    {link.category && <p className="text-xs text-muted-foreground mt-1">{link.category}</p>}
                    <p className="text-xs text-muted-foreground truncate mt-1">{link.url}</p>
                    {link.notes && <p className="text-xs text-muted-foreground mt-2 italic">{link.notes}</p>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setEditing(link); setFormData({ label: link.label, url: link.url, category: link.category || "", notes: link.notes || "" }); setShowDialog(true); }}>
                        <Pencil className="h-4 w-4 mr-2" />Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(link.id)} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Link" : "Add Link"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input value={formData.label} onChange={(e) => setFormData({...formData, label: e.target.value})} required placeholder="e.g., Google Drive" />
            </div>
            <div className="space-y-2">
              <Label>URL *</Label>
              <Input value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} required placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} placeholder="e.g., Design, Documents" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Optional notes about this link" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">{editing ? "Update" : "Add"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Contacts Section Component
function ContactsSection({ contacts, clientId, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: "", role: "", email: "", phone: "", notes: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await contactsAPI.update(editing.id, formData);
        toast.success("Contact updated");
      } else {
        await contactsAPI.create({ ...formData, client_id: clientId });
        toast.success("Contact added");
      }
      setShowDialog(false);
      setEditing(null);
      setFormData({ name: "", role: "", email: "", phone: "", notes: "" });
      onRefresh();
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await contactsAPI.delete(id);
      toast.success("Contact deleted");
      onRefresh();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Contacts</h2>
        <Button onClick={() => { setEditing(null); setFormData({ name: "", role: "", email: "", phone: "", notes: "" }); setShowDialog(true); }} data-testid="add-contact-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No contacts yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {contacts.map((contact) => (
            <Card key={contact.id} className="group">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{contact.name}</h3>
                    {contact.role && <p className="text-sm text-muted-foreground">{contact.role}</p>}
                    <div className="mt-2 space-y-1">
                      {contact.email && <p className="text-sm">{contact.email}</p>}
                      {contact.phone && <p className="text-sm">{contact.phone}</p>}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setEditing(contact); setFormData({ name: contact.name, role: contact.role || "", email: contact.email || "", phone: contact.phone || "", notes: contact.notes || "" }); setShowDialog(true); }}>
                        <Pencil className="h-4 w-4 mr-2" />Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(contact.id)} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Input value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} placeholder="e.g., Project Manager" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">{editing ? "Update" : "Add"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Project Tracker Section Component
function ProjectTrackerSection({ tasks, projects, timeEntries, clientId, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    project_id: "",
    due_date: "",
    priority: "medium",
  });

  const handleDownloadCSV = () => {
    window.location.href = exportAPI.projectTracker({ client_id: clientId });
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await tasksAPI.update(task.id, { status: newStatus });
      toast.success("Status updated");
      onRefresh();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await tasksAPI.create({
        ...formData,
        client_id: clientId,
        project_id: formData.project_id || null,
        status: "new",
      });
      toast.success("Tracker item created");
      setShowDialog(false);
      setFormData({ title: "", description: "", project_id: "", due_date: "", priority: "medium" });
      onRefresh();
    } catch (err) {
      toast.error("Failed to create tracker item");
    }
  };

  const getTaskDuration = (taskId) => {
    return timeEntries
      .filter((e) => e.task_id === taskId)
      .reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  };

  const getTaskBillableHours = (taskId) => {
    const mins = timeEntries
      .filter((e) => e.task_id === taskId && e.billable)
      .reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
    return Math.round((mins / 60) * 100) / 100;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <BarChart2 className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Project Tracker</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadCSV} className="gap-2" data-testid="download-tracker-csv-btn">
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
          <Button onClick={() => setShowDialog(true)} data-testid="add-tracker-item-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Tracker Item
          </Button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No tracker items yet. Add your first task to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date Created</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Total Duration</TableHead>
                <TableHead>Billable Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((task) => {
                const totalMin = getTaskDuration(task.id);
                const billableHrs = getTaskBillableHours(task.id);
                return (
                  <TableRow key={task.id}>
                    <TableCell className="font-medium">{task.title}</TableCell>
                    <TableCell>{projects.find((p) => p.id === task.project_id)?.name || "-"}</TableCell>
                    <TableCell>
                      <Select
                        value={task.status}
                        onValueChange={(v) => handleStatusChange(task, v)}
                      >
                        <SelectTrigger className="h-7 w-36 text-xs">
                          <SelectValue>
                            <Badge className={getStatusColor(task.status)}>
                              {TASK_STATUSES.find((s) => s.value === task.status)?.label || task.status}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {TASK_STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              <Badge className={getStatusColor(s.value)}>{s.label}</Badge>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{task.created_at ? formatDate(task.created_at) : "-"}</TableCell>
                    <TableCell>{task.due_date ? formatDate(task.due_date) : "-"}</TableCell>
                    <TableCell className="font-mono">
                      {totalMin > 0 ? formatDuration(totalMin) : "-"}
                    </TableCell>
                    <TableCell className="font-mono">
                      {billableHrs > 0 ? `${billableHrs}h` : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tracker Item</DialogTitle>
            <DialogDescription>
              Create a new task to track. Status will start as NEW and automatically move to In Progress once a timer is started.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Task Name *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Design landing page"
                required
                data-testid="tracker-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional details..."
              />
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <Select
                value={formData.project_id}
                onValueChange={(v) => setFormData({ ...formData, project_id: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
